import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import snowflake from "../../../assets/icons/loading-snowflake.svg";
import Card from "./card/Card";
import { useUser, UserRole } from "../../../services/UserContext";
import useFetch from "../../../services/functions/useFetch";
import CustomAlert from "../../CustomAlert";
import { Link } from "react-router-dom";
import useAnimation from "../../../services/functions/useAnimation";
import SearchButton from "../../utils/buttons/SearchButton";
import Popup from "./card/modify/Popup";

export interface Exercise {
  id: number;
  title: string;
  description: string;
  thumbnail: number;
}

// Define the shape of the CardData
interface CardData {
  exercises: Exercise[];
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ContentList() {
  // extract age and skill id from URL
  const query = useQuery();
  const age: string = query.get("age") || "0";
  const skill: string = query.get("skill") || "0";

  const { role } = useUser();
  const { data, error, isPending, fetchData } = useFetch<CardData>();

  const exercisesPopulated = useRef(false);

  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]); // store filtered exercises

  const [searchValue, setSearchValue] = useState<string>(""); // bind the search input value
  const [inputVisible, setInputVisible] = useState(false); // tracks visibility of the search input
  const [searchAnimationClass, setAnimationClass] = useState(""); // gives search container the call for the animation
  const [popupState, setPopupState] = useState<{
    index: number;
    title: string;
    visible: boolean;
  }>({ index: -1, title: "", visible: false });

  const { slideIn, slideOutAll, slideOutItem } = useAnimation();

  useEffect(() => {
    slideIn(".card__wrapper"); // * applying of smooth animation when elements appear
  }, [data?.exercises]);

  useEffect(() => {
    if (popupState.visible) {
      document.documentElement.classList.add("lock");
    } else document.documentElement.classList.remove("lock");
  }, [popupState.visible]);

  useEffect(() => {
    fetchData(`/get/exercises/${age}/${skill}`, "GET", {
      doOnSuccess: (response: CardData) => {
        setFilteredExercises(response?.exercises);
      },
      doOnError: () => {
        console.error("Failed to fetch exercises.");
      },
    });
  }, []);

  useEffect(() => {
    if (data?.exercises && !exercisesPopulated.current) {
      exercisesPopulated.current = true;
      setFilteredExercises(data.exercises); // populate with exercises from data
    }
  }, [data]);

  const toggleSearch = () => {
    if (inputVisible) {
      setAnimationClass("search-container-slide-out");
      clearSearch();
      setTimeout(() => {
        setInputVisible(false);
        setAnimationClass("");
      }, 350);
    } else {
      setInputVisible(true);
      setAnimationClass("search-container-slide-in");
    }
  };

  const throttleTimeout = useRef<NodeJS.Timeout | number | null>(null);
  const handleSearch = (value: string) => {
    setSearchValue(value);

    if (!data?.exercises) return;

    if (throttleTimeout.current !== null) {
      clearTimeout(throttleTimeout.current);
    }

    // set a new throttling timeout to process filtering
    throttleTimeout.current = setTimeout(async () => {
      const updatedFiltered = data.exercises.filter((exercise) =>
        exercise.title.toLowerCase().includes(value)
      );

      const removing = new Set(filteredExercises.map((ex) => ex.id));
      updatedFiltered.forEach((ex) => removing.delete(ex.id));

      await slideOutAll(".card__wrapper", "", 100);
      setFilteredExercises(updatedFiltered);
      setTimeout(async () => {
        await slideIn(".card__wrapper");
      }, 0); // forces react to update dom

      throttleTimeout.current = null;
    }, 400);
  };

  const clearSearch = () => {
    if (searchValue) handleSearch("");
  };

  // Check if the user can create new exercises (Admin or Editor)
  const canCreate = role === UserRole.ADMIN || role === UserRole.EDITOR;

  return (
    <>
      {error && <CustomAlert message={error} type="error" />}
      {isPending ? (
        <img src={snowflake} alt="loading" className="loading-snowflake" />
      ) : (
        <>
          {popupState.visible && (
            <Popup
              popup={popupState}
              setPopup={setPopupState}
              exerciseList={filteredExercises}
              setExerciseList={setFilteredExercises}
            ></Popup>
          )}
          <div
            className="search-combine"
            onDragStart={(e) => e.preventDefault()}
          >
            <SearchButton isVisible={inputVisible} onClick={toggleSearch} />
            {inputVisible && (
              <div className={`search-container ${searchAnimationClass}`}>
                <div className="input-container">
                  <input
                    autoFocus
                    placeholder="Suche . . ."
                    className="search-input"
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value.toLowerCase())}
                  />
                  <span className="clear-icon" onClick={clearSearch}>
                    &times;
                  </span>
                </div>
              </div>
            )}
          </div>
          {canCreate && (
            <div className="exercise-container">
              <Link to={`/new?age=${age}&skill=${skill}`} className="button">
                Neue Übung erstellen
              </Link>
            </div>
          )}
          {data && (
            <>
              <div className="card__container">
                {filteredExercises.map((card) => (
                  <div
                    onClick={async () =>
                      await slideOutItem(
                        ".card__wrapper",
                        card.id.toString(),
                        `/detail?id=${card.id}`
                      )
                    }
                    id={card.id.toString()}
                    className="card__wrapper"
                    key={card.id}
                  >
                    <Card
                      id={card.id}
                      title={card.title}
                      description={card.description}
                      thumbnail={card.thumbnail}
                      setVisible={setPopupState}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

export default ContentList;
